// Modal de envio de localização.
//   - Card amarelo: informação do endereço da loja (não é clicável)
//   - Campo opcional: colar link do Google Maps pra outro endereço
//   - Botão azul: envia a loja (se campo vazio) ou o link colado
//
// Backend: POST /api/conversas/:id/mensagens/localizacao { latitude, longitude, nome, endereco }
import { useEffect, useRef, useState } from 'react';
import { MapPin, X, Loader2, Navigation } from 'lucide-react';
import { atendimentoApi } from '../lib/api';

// Coordenada confirmada ao vivo pelo Ruan (2026-05-29)
const LOJA: { latitude: number; longitude: number; nome: string; endereco: string } = {
  latitude: -16.51594362722168,
  longitude: -50.368526458740234,
  nome: 'FIODECOBRE',
  endereco: 'São Luís de Montes Belos - GO',
};

// Endereço completo enviado como texto logo após o pino da loja (2026-07-07)
const ENDERECO_TEXTO = 'Av Rio da Prata No.920 Qd.78 Lt.09,\nSetor Montes Belos - Sao Luis de Montes Belos';

interface Props {
  conversaId: number;
  onClose: () => void;
  onEnviado?: () => void;
}

function extrairCoordenadas(link: string): { latitude: number; longitude: number } | null {
  const padroes = [
    /@(-?\d+\.\d+),(-?\d+\.\d+)/,
    /[?&]q=(-?\d+\.\d+),(-?\d+\.\d+)/,
    /[?&]ll=(-?\d+\.\d+),(-?\d+\.\d+)/,
    /\/(-?\d+\.\d+),(-?\d+\.\d+)/,
  ];
  for (const re of padroes) {
    const m = link.match(re);
    if (m) {
      const lat = parseFloat(m[1]!);
      const lng = parseFloat(m[2]!);
      if (Number.isFinite(lat) && Number.isFinite(lng)) return { latitude: lat, longitude: lng };
    }
  }
  return null;
}

export function ModalEnviarLocalizacao({ conversaId, onClose, onEnviado }: Props) {
  const [link, setLink] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !enviando) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, enviando]);

  async function enviar(payload: { latitude: number; longitude: number; nome?: string; endereco?: string }, comEnderecoTexto = false) {
    if (enviando) return;
    setEnviando(true);
    setErro(null);
    try {
      await atendimentoApi.enviarLocalizacao(conversaId, payload);
      // Loja: manda o endereço completo por escrito logo depois do pino.
      if (comEnderecoTexto) {
        await atendimentoApi.enviarTexto(conversaId, { texto: ENDERECO_TEXTO });
      }
      onEnviado?.();
      onClose();
    } catch (e: any) {
      setErro(e?.message || 'Falha ao enviar localização');
      setEnviando(false);
    }
  }

  function enviarAcao() {
    const linkTrimado = link.trim();
    if (linkTrimado) {
      const coords = extrairCoordenadas(linkTrimado);
      if (!coords) {
        setErro('Não consegui extrair as coordenadas. Cole o link completo do Google Maps (não o link curto goo.gl).');
        return;
      }
      enviar(coords);
    } else {
      enviar(LOJA, true);
    }
  }

  const coordsDoLink = link.trim() ? extrairCoordenadas(link.trim()) : null;
  const linkInvalido = link.trim() && !coordsDoLink;

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-2" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div
        className="modal-conteudo rounded-lg shadow-xl overflow-hidden flex flex-col"
        style={{ backgroundColor: 'var(--modal-bg, #fff)', width: '100%', maxWidth: 480 }}
      >
        {/* Header */}
        <div className="modal-header flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
          <div className="flex items-center gap-2">
            <MapPin size={18} />
            <h3 className="font-semibold">Enviar localização</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={enviando}
            className="btn-fechar-modal p-1 rounded hover:bg-black/5"
            title="Fechar (Esc)"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-4 flex flex-col gap-4">
          {/* Info da loja — só leitura */}
          <div
            className="flex items-center gap-3 w-full rounded-xl border-2 px-4 py-3"
            style={{ backgroundColor: '#fde68a', borderColor: '#b45309', color: '#000' }}
          >
            <Navigation size={22} className="flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="font-bold text-sm">Loja FIODECOBRE</p>
              <p className="text-xs opacity-70 truncate">{LOJA.endereco}</p>
              <p className="text-[11px] opacity-70 mt-1 leading-tight whitespace-pre-line">
                {'Envia o pino + o endereço escrito:\n' + ENDERECO_TEXTO}
              </p>
            </div>
          </div>

          {/* Divisor */}
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <div className="flex-1 h-px bg-gray-200" />
            ou cole um link do Google Maps
            <div className="flex-1 h-px bg-gray-200" />
          </div>

          {/* Campo de link + botão */}
          <div className="flex flex-col gap-2">
            <input
              ref={inputRef}
              type="url"
              value={link}
              onChange={(e) => { setLink(e.target.value); setErro(null); }}
              placeholder="https://maps.google.com/..."
              className="no-upper w-full px-3 py-2 border rounded-lg outline-none"
              style={{ fontSize: '15px', textTransform: 'none' }}
              onKeyDown={(e) => { if (e.key === 'Enter') enviarAcao(); }}
            />
            {coordsDoLink && (
              <p className="text-xs text-green-700 px-1">
                Coordenadas detectadas: {coordsDoLink.latitude.toFixed(5)}, {coordsDoLink.longitude.toFixed(5)}
              </p>
            )}
            <button
              type="button"
              onClick={enviarAcao}
              disabled={enviando || !!linkInvalido}
              className="self-end flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ backgroundColor: 'var(--accent, #2563eb)', color: 'var(--accent-fg, #fff)' }}
            >
              {enviando ? <Loader2 size={14} className="animate-spin" /> : <MapPin size={14} />}
              Enviar localização
            </button>
          </div>
        </div>

        {erro && (
          <div className="px-4 py-2 text-sm border-t" style={{ backgroundColor: '#fef2f2', color: '#b91c1c', borderColor: 'rgba(0,0,0,0.08)' }}>
            {erro}
          </div>
        )}
      </div>
    </div>
  );
}
